import { useMemo, useState } from "react";
import DataListHeader from "components/DataListHeader";
import DataTable from "components/DataTable/DataTable";
import EmptyPlaceholder from "components/EmptyPlaceholder/EmptyPlaceholder";
import PageComponent from "components/Page/PageComponent";
import AccordionCore from "components/Shared/Accordion/AccordionCore";
import { useGetAllActivityItemsQuery } from "features/activity/activitySlice";
import { useGetClientsQuery } from "features/clients/clientSlice";
import { useGetLotsQuery } from "features/lots/lotSlice";
import { useGetPricingManagementByLotIdQuery } from "features/pricing/pricingSlice";
import { useGetVendorsQuery } from "features/vendor/vendorSlice";
import useSortOptions from "hooks/useSortOptions";
import { orderBy } from "lodash";
import { arrayToObject } from "utils/object-util";
import { PricingByLotStyle } from "./PricingByLotStyle";
import {
	groupRowsByVendor,
	NO_ITEMS_MESSAGE,
	NO_LOT_SELECTED_TEXT,
	groupVehiclePricing,
	UNKNOWN_VENDOR_LABEL,
	columns,
	ALL_CLIENTS_LABEL
} from "./PricingByLotConsts";
import { fieldTypes } from "services/filterService";
import FormatService from "services/formatService";
import { Grid } from "@material-ui/core";


const PricingByLot = () => {
	const classes = PricingByLotStyle();
	const [sortOptions, sortData] = useSortOptions();
	const [filters, setFilters] = useState({ lotId: null });

	const selectedLotId = filters?.lotId?.value;

	const onDropFilter = (value, prop) => {
		setFilters({ ...filters, [prop]: { value } });
	};

	const { data: lots, isLoading: isLoadingLots } = useGetLotsQuery();
	const { data: clients, isLoading: isLoadingClients } = useGetClientsQuery(null);
	const { data: vendors, isLoading: isLoadingVendors } = useGetVendorsQuery();
	const { data: activityItems, isLoading: isLoadingActivityItems } = useGetAllActivityItemsQuery();
	const { data: pricingData, isFetching: isLoadingPricing } = useGetPricingManagementByLotIdQuery(selectedLotId, {
		skip: !selectedLotId,
	});

	const clientLookup = useMemo(() => arrayToObject(clients), [clients]);
	const vendorLookup = useMemo(() => arrayToObject(vendors), [vendors]);
	const activityItemLookup = useMemo(() => arrayToObject(activityItems), [activityItems]);

	const filterConfig = [
		{ type: fieldTypes.LOT, size: 2, title: 'Lot', key: 'lotId', values: lots },
	];

	const vehiclePricingGroupedData = useMemo(
		() => (pricingData ? groupVehiclePricing(pricingData) : []),
		[pricingData]
	);

	const rows = useMemo(
		() => (vehiclePricingGroupedData || []).map((row, index) => ({
			...row,
			id: row?.id ?? `${row?.activityItemId || 'activity'}-${row?.clientId ?? 'default'}-${index}`,
			activityName: activityItemLookup?.[row?.activityItemId]?.name,
			vendorCost: row?.price,
			formattedVendorCost: row?.price != null ? FormatService.formatCurrency(row.price) : '',
			clientDisplayName: clientLookup?.[row?.clientId]?.name ?? ALL_CLIENTS_LABEL,
			vendorDisplayName: vendorLookup?.[row?.vendorId]?.name ?? UNKNOWN_VENDOR_LABEL,
			vehicleTypeMargin: row?.vehicleTypeMargin ?? row?.margin,
		})),
		[vehiclePricingGroupedData, clientLookup, vendorLookup, activityItemLookup]
	);

	const groupedByVendorRows = useMemo(
		() => groupRowsByVendor(rows).sort((a, b) => a.vendorName.localeCompare(b.vendorName)),
		[rows]
	);

	const isLoading = isLoadingLots || isLoadingClients || isLoadingVendors || isLoadingActivityItems || isLoadingPricing;

	return (
		<PageComponent
			header={
				<DataListHeader
					data={rows}
					dropDownsConfig={filterConfig}
					filters={filters}
					onDropFilter={onDropFilter}
				/>
			}
		>
			{!selectedLotId || groupedByVendorRows.length === 0 ? (
				<EmptyPlaceholder text={NO_LOT_SELECTED_TEXT} />
			) : (
				<Grid container direction="column" className={classes.vendorSections}>
					{groupedByVendorRows.map((vendorGroup, index) => (
						<Grid item className={classes.vendorSection} key={vendorGroup.vendorName}>
							<AccordionCore
								headerText={`${vendorGroup.vendorName} (${vendorGroup.rows.length} Activities)`}
								open={index === 0}
								className={classes.vendorAccordion}
								classAccordionContent={classes.vendorAccordionContent}
							>
								<div style={{ '--table-height': `${vendorGroup.rows.length * 60 + 90}px`, height: vendorGroup.rows.length * 60 + 90, width: '100%' }}>
									<DataTable
										columns={columns}
										rows={orderBy(
											vendorGroup.rows,
											sortOptions.columnToSort || 'activityName',
											sortOptions.sortDirection || 'asc'
										)}
										rowIdentifier="id"
										onSort={sortData}
										sortDirection={sortOptions.sortDirection}
										columnToSort={sortOptions.columnToSort}
										noItemsMessage={NO_ITEMS_MESSAGE}
										headerStyle={{
											paddingLeft: "1rem",
										}}
										data={vendorGroup.rows}
										isLoading={isLoading}
										className={classes.vendorTableCompact}
									/>
								</div>
							</AccordionCore>
						</Grid>
					))}
				</Grid>
			)}
		</PageComponent>
	);
};

export default PricingByLot;
